import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '@/js/firebase'
import { uploadRawFile } from '@/services/bunnyUploadService'
import { BUNNY_UPLOAD_CONTEXT, BUNNY_APP_NAMESPACE } from '@/utils/bunnyConstants'
import { logger as loggerService } from '@/services/loggerService'

class FeedbackService {
  constructor() {
    this.collectionName = 'feedback'
  }

  // Deep clean function to remove undefined values from nested objects
  cleanData(obj) {
    if (obj === null || obj === undefined) {
      return null
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.cleanData(item)).filter(item => item !== undefined)
    }
    
    if (typeof obj === 'object' && obj.constructor === Object) {
      const cleaned = {}
      for (const [key, value] of Object.entries(obj)) {
        const cleanedValue = this.cleanData(value)
        if (cleanedValue !== undefined) {
          cleaned[key] = cleanedValue
        }
      }
      return Object.keys(cleaned).length > 0 ? cleaned : null
    }
    
    return obj
  }

  async createFeedback(feedbackData) {
    try {
      loggerService.info('[FeedbackService] Creating new feedback', { type: feedbackData.type })
      
      // Deep clean the feedback data to remove any undefined values
      const cleanedData = this.cleanData(feedbackData)
      
      if (!cleanedData) {
        throw new Error('No valid data to save')
      }
      
      const feedbackWithTimestamp = {
        ...cleanedData,
        timestamp: serverTimestamp(),
        status: cleanedData.status || 'new'
      }
      
      // Log the data being sent for debugging
      loggerService.info('[FeedbackService] Cleaned feedback data', feedbackWithTimestamp)
      if (feedbackWithTimestamp.consoleLogs) {
        loggerService.info('[FeedbackService] Console logs included:', feedbackWithTimestamp.consoleLogs.length, 'entries')
      } else {
        loggerService.warn('[FeedbackService] No console logs in feedback data')
      }
      
      const docRef = await addDoc(collection(db, this.collectionName), feedbackWithTimestamp)
      
      loggerService.info('[FeedbackService] Feedback created successfully', { id: docRef.id })
      return docRef.id
    } catch (error) {
      loggerService.error('[FeedbackService] Error creating feedback', error)
      // Log the original data for debugging
      loggerService.error('[FeedbackService] Original feedback data that caused error', feedbackData)
      throw error
    }
  }

  async getAllFeedback() {
    try {
      loggerService.info('[FeedbackService] Fetching all feedback')
      
      const q = query(collection(db, this.collectionName), orderBy('timestamp', 'desc'))
      const querySnapshot = await getDocs(q)
      
      const feedback = []
      querySnapshot.forEach((doc) => {
        feedback.push({
          id: doc.id,
          ...doc.data()
        })
      })
      
      loggerService.info('[FeedbackService] Fetched feedback items', { count: feedback.length })
      return feedback
    } catch (error) {
      loggerService.error('[FeedbackService] Error fetching feedback', error)
      throw error
    }
  }

  async updateFeedback(feedbackId, updates) {
    try {
      loggerService.info('[FeedbackService] Updating feedback', { id: feedbackId, updates })
      
      const feedbackRef = doc(db, this.collectionName, feedbackId)
      await updateDoc(feedbackRef, {
        ...updates,
        updatedAt: serverTimestamp()
      })
      
      loggerService.info('[FeedbackService] Feedback updated successfully', { id: feedbackId })
    } catch (error) {
      loggerService.error('[FeedbackService] Error updating feedback', error)
      throw error
    }
  }

  async deleteFeedback(feedbackId) {
    try {
      loggerService.info('[FeedbackService] Deleting feedback', { id: feedbackId })

      await deleteDoc(doc(db, this.collectionName, feedbackId))

      loggerService.info('[FeedbackService] Feedback deleted successfully', { id: feedbackId })
    } catch (error) {
      loggerService.error('[FeedbackService] Error deleting feedback', error)
      throw error
    }
  }

  async uploadScreenshot(file) {
    try {
      loggerService.info('[FeedbackService] Uploading screenshot', { name: file.name, size: file.size })

      // Feedback screenshots are transient — they accompany a bug
      // report and become stale once the issue is resolved. Park them
      // under app/feedback-screenshots/ so a sweep can clear them without
      // touching business files.
      const result = await uploadRawFile({
        file,
        context: {
          type: BUNNY_UPLOAD_CONTEXT.TEMP_ATTACHMENT,
          namespace: BUNNY_APP_NAMESPACE.FEEDBACK_SCREENSHOT,
          contextId: 'general',
        },
      })

      loggerService.info('[FeedbackService] Screenshot uploaded', { path: result.path })
      // Return the PATH alongside the URL. `result.url` is a 30-day signed
      // Bunny URL; storing only that made the screenshot unrecoverable once it
      // expired (permanent 403 in FeedbackDetailModal, no way to re-mint).
      // The path lets the read side re-sign on demand via refreshBunnyUrl.
      return { url: result.url, path: result.path }
    } catch (error) {
      loggerService.error('[FeedbackService] Error uploading screenshot', error)
      throw error
    }
  }

  // Bunny temp attachments have no per-URL delete primitive; a
  // periodic sweep handles cleanup. Kept as a no-op so existing
  // callers don't break.
   
  async deleteScreenshot(_screenshotUrl) {}
}

export const feedbackService = new FeedbackService()